import {
  isPeopleSettingsPath,
  PEOPLE_SETTINGS_READ_ROLES,
  PEOPLE_SETTINGS_TOOL_READ_ROLES,
} from './people-settings';

import {
  findClusterForPath,
  SETTINGS_CROSS_SECTION_LEAVES,
} from '@/components/nav-v2/settings/settings-ia';
import { getAllowedRolesForWorkflowCapability } from '@/lib/capabilities';
import { COMPLIANCE_SETTINGS_WRITE_ROLES } from '@/lib/compliance-settings/roles';
import {
  DHDA_INTEGRATION_ROLES,
  HI_INTEGRATION_SETUP_ROLES,
} from '@/lib/integrations/government/access';
import {
  PAY_EVENT_FACT_QUEUE_ROLES,
  isPayEventFactQueuePath,
} from '@/lib/payroll/pay-event-fact-integrity';

import {
  SETTINGS_FINANCE_ROLES,
  SETTINGS_OWN_NOTIFICATION_ROLES,
  isOwnNotificationPreferencePath,
  OWN_NOTIFICATION_PREFERENCE_PATH,
  SETTINGS_INTEGRATIONS_HUB_ROLES,
  SETTINGS_PAYROLL_PROVIDER_ROLES,
  SETTINGS_PAY_ROLE_ROLES,
  SETTINGS_RETENTION_ROLES,
  SETTINGS_SERVICES_AUSTRALIA_ROLES,
  SETTINGS_SYSTEM_OPERATOR_ROLES,
  SETTINGS_TENANT_ADMIN_ROLES,
  SETTINGS_XERO_ROLES,
  SIGNATURE_TEMPLATE_SETTINGS_ROLES,
} from '@/lib/settings/settings-access-roles';

export {
  SETTINGS_FINANCE_ROLES,
  SETTINGS_OWN_NOTIFICATION_ROLES,
  isOwnNotificationPreferencePath,
  OWN_NOTIFICATION_PREFERENCE_PATH,
  SETTINGS_INTEGRATIONS_HUB_ROLES,
  SETTINGS_PAYROLL_PROVIDER_ROLES,
  SETTINGS_PAY_ROLE_ROLES,
  SETTINGS_RETENTION_ROLES,
  SETTINGS_SERVICES_AUSTRALIA_ROLES,
  SETTINGS_SYSTEM_OPERATOR_ROLES,
  SETTINGS_TENANT_ADMIN_ROLES,
  SETTINGS_XERO_ROLES,
  SIGNATURE_TEMPLATE_SETTINGS_ROLES,
};

const TENANT_CONFIGURE_ROLES = getAllowedRolesForWorkflowCapability('tenant.configure');

/**
 * Authoritative RoleGuard cohort for a settings pathname.
 * Keep in lockstep with `settings-ia.ts` leaf `roles` and `route-access.ts`.
 */
const CROSS_SECTION_HREFS = new Set<string>(SETTINGS_CROSS_SECTION_LEAVES);

export function resolveBaseSettingsAllowedRoles(pathname: string): readonly string[] {
  if (pathname === '/settings/people' || pathname === '/settings')
    return PEOPLE_SETTINGS_READ_ROLES;
  if (isPeopleSettingsPath(pathname) && !pathname.startsWith('/settings/onboarding/client-packs'))
    return PEOPLE_SETTINGS_TOOL_READ_ROLES;
  // Facility managers reach the same page for assigned-facility overrides;
  // the page and controlled RPC still refuse tenant-wide edits for them.
  if (pathname.startsWith('/settings/compliance')) {
    return [...COMPLIANCE_SETTINGS_WRITE_ROLES, 'FACILITY_MANAGER'];
  }
  const iaMatch = findClusterForPath(pathname);
  if (iaMatch?.item.roles && CROSS_SECTION_HREFS.has(iaMatch.item.href)) {
    return iaMatch.item.roles;
  }

  if (pathname.startsWith('/settings/webhooks')) {
    return SETTINGS_SYSTEM_OPERATOR_ROLES;
  }
  if (pathname.startsWith('/settings/notification-routing')) {
    return SETTINGS_TENANT_ADMIN_ROLES;
  }
  if (isOwnNotificationPreferencePath(pathname)) {
    // A person's own notification choices are not tenant configuration. This
    // must be checked BEFORE the /settings admin default below, which is what
    // was refusing every non-admin who followed an email footer here.
    return SETTINGS_OWN_NOTIFICATION_ROLES;
  }
  if (pathname.startsWith('/settings/service-execution')) {
    // Must stay the same pair as `set_service_execution_v2_pack_state_controlled`
    // gates on. A wider cohort here shows a console whose every button is
    // refused at the database.
    return SETTINGS_TENANT_ADMIN_ROLES;
  }
  if (pathname.startsWith('/settings/integrations/services-australia')) {
    return SETTINGS_SERVICES_AUSTRALIA_ROLES;
  }
  if (pathname.startsWith('/settings/integrations/hi')) {
    return HI_INTEGRATION_SETUP_ROLES;
  }
  if (pathname.startsWith('/settings/integrations/dhda')) {
    // The route admits B2G data roles and the tenant/platform configuration
    // cohort. Server actions and separately rendered panels enforce each role.
    return DHDA_INTEGRATION_ROLES;
  }
  if (pathname.startsWith('/settings/integrations/xero')) {
    return SETTINGS_XERO_ROLES;
  }
  if (pathname.startsWith('/settings/integrations')) {
    return SETTINGS_INTEGRATIONS_HUB_ROLES;
  }
  if (pathname.startsWith('/settings/feature-flags')) {
    return SETTINGS_SYSTEM_OPERATOR_ROLES;
  }
  if (pathname.startsWith('/settings/signature-templates')) {
    return SIGNATURE_TEMPLATE_SETTINGS_ROLES;
  }
  if (pathname.startsWith('/settings/funding-authorisations')) {
    return SETTINGS_FINANCE_ROLES;
  }
  if (pathname.startsWith('/settings/billing')) {
    return SETTINGS_FINANCE_ROLES;
  }
  if (pathname.startsWith('/settings/payroll/activation')) {
    return SETTINGS_SYSTEM_OPERATOR_ROLES;
  }
  if (pathname.startsWith('/settings/payroll/aba-sender')) {
    return SETTINGS_FINANCE_ROLES;
  }
  if (pathname.startsWith('/settings/payroll/profile')) {
    return SETTINGS_PAYROLL_PROVIDER_ROLES;
  }
  if (pathname.startsWith('/settings/payroll/provider-responsibilities')) {
    return SETTINGS_PAYROLL_PROVIDER_ROLES;
  }
  if (pathname.startsWith('/settings/payroll/pay-roles')) {
    return SETTINGS_PAY_ROLE_ROLES;
  }
  if (pathname === '/settings/payroll' || pathname === '/settings/payroll/') {
    return SETTINGS_PAYROLL_PROVIDER_ROLES;
  }
  if (pathname.startsWith('/settings/retention')) {
    return SETTINGS_RETENTION_ROLES;
  }
  if (isPayEventFactQueuePath(pathname)) {
    // The pay-fact review queue leaf: whoever may approve pay facts may open
    // the page that holds Approve / Supersede and re-submit. The rest of the
    // EBA workflow stays with tenant configuration.
    return PAY_EVENT_FACT_QUEUE_ROLES;
  }
  if (pathname.startsWith('/settings/payroll')) {
    return TENANT_CONFIGURE_ROLES;
  }
  if (
    pathname.startsWith('/settings/modules') ||
    pathname.startsWith('/settings/portal') ||
    pathname.startsWith('/settings/cert-allowance-registry') ||
    pathname.startsWith('/settings/cm-task-types') ||
    pathname.startsWith('/settings/assessments') ||
    pathname.startsWith('/settings/document-templates') ||
    pathname.startsWith('/settings/onboarding') ||
    pathname.startsWith('/settings/onboarding-packs') ||
    pathname.startsWith('/settings/workflows') ||
    pathname.startsWith('/settings/security')
  ) {
    return TENANT_CONFIGURE_ROLES;
  }
  return SETTINGS_TENANT_ADMIN_ROLES;
}

export function isSettingsSystemOperatorRole(role: string | null | undefined): boolean {
  return role === 'SYSTEM_ADMIN';
}
